"use client";

import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shadcn/ui/dialog";
import { CTAWrapper } from "@/components/global/utils/CTAWrapper";
import { GROQButton } from "@/sanity/lib/definitions";

// Carbon "Unlocked" glyph — an open padlock (Figma 2039:5355).
const UnlockedIcon = () => (
  <svg viewBox="0 0 32 32" fill="none" className="w-full h-full" aria-hidden xmlns="http://www.w3.org/2000/svg">
    <path
      d="M23 2a7 7 0 00-7 7v5H6a2 2 0 00-2 2v14a2 2 0 002 2h16a2 2 0 002-2V16a2 2 0 00-2-2H18V9a5 5 0 015-5 5 5 0 015 5v3h2V9a7 7 0 00-7-7zM22 16v14H6V16h16z"
      fill="currentColor"
    />
  </svg>
);

// Close ("X") glyph — light, for the dark modal (Figma 2039:5361).
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="w-full h-full" aria-hidden xmlns="http://www.w3.org/2000/svg">
    <path
      d="M13.0606 12L19.5 5.56058L18.4394 4.5L12 10.9394L5.56072 4.5L4.5 5.56058L10.9394 12L4.5 18.4394L5.56072 19.5L12 13.0606L18.4394 19.5L19.5 18.4394L13.0606 12Z"
      fill="currentColor"
    />
  </svg>
);

const DEFAULT_HEADING = "Want to keep exploring?";
const DEFAULT_BODY =
  "Register now for free and explore Trading Courses, our Community, exclusive rewards & much more! Everything made by Hedge Fund Traders, World Trading Champions & Market Makers.";
const DEFAULT_CTA_LABEL = "Register Now";

interface UnlockDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  heading?: string;
  body?: string;
  ctaLabel?: string;
  cta?: GROQButton;
}

/**
 * The "Unlock" popup shown whenever a visitor clicks anything inside the Student
 * Area demo (Figma 2039-5351). Controlled by the demo shell; a dark card with a
 * padlock badge, headline, body and a single "Register now" CTA.
 */
export default function UnlockDialog({
  open,
  onOpenChange,
  heading = DEFAULT_HEADING,
  body = DEFAULT_BODY,
  ctaLabel = DEFAULT_CTA_LABEL,
  cta,
}: UnlockDialogProps) {
  const button = (
    <span className="flex items-center justify-center w-full rounded-[1.25rem] bg-[#98d8ff] h-[6rem] px-[3rem] font-geist font-semibold capitalize text-black text-[2.25rem] leading-[1.5] transition-opacity hover:opacity-90">
      {ctaLabel}
    </span>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* `[&>button]:hidden` drops the shadcn default (dark) close X so we can
          render a light one that reads on the dark card. */}
      <DialogContent className="!w-[min(65rem,calc(100%-4rem))] rounded-[1.5rem] bg-[#212121] px-[3rem] pt-[4rem] pb-[3rem] [&>button]:hidden">
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle>{heading}</DialogTitle>
            <DialogDescription>{body}</DialogDescription>
          </DialogHeader>
        </VisuallyHidden>

        <div className="flex flex-col items-center w-full gap-[4rem]">
          <div className="flex flex-col items-center w-full gap-[4rem]">
            <span className="flex items-center justify-center rounded-full bg-white/[0.06] text-white p-[2.5rem]">
              <span className="block w-[4rem] h-[4rem]">
                <UnlockedIcon />
              </span>
            </span>

            <div className="flex flex-col items-center w-full text-center text-white gap-[2.5rem]">
              <p className="m-0 font-news text-[4rem] leading-[1.3] tracking-[-0.08rem]">
                {heading}
              </p>
              <p className="m-0 font-geist opacity-70 text-[2.25rem] leading-[1.5] max-w-[52.25rem]">
                {body}
              </p>
            </div>
          </div>

          {cta?.cta ? (
            <CTAWrapper cta={cta.cta} className="!block w-full">
              {button}
            </CTAWrapper>
          ) : (
            button
          )}
        </div>

        {/* Light close X (top-right) — replaces the hidden default. */}
        <DialogClose className="absolute right-[2.5rem] top-[2.5rem] text-white/80 transition-opacity hover:opacity-100 focus:outline-none">
          <span className="block w-[3rem] h-[3rem]">
            <CloseIcon />
          </span>
          <span className="sr-only">Close</span>
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
}
